<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { ActionItem, ActionsGroup, ActionsMenu, VButton } from '@/modules/shared/components'
import VPopUpModal from '@/modules/shared/components/v-pop-up-modal.vue'
import { useModal } from '@/modules/shared/composables/use-modal'
import { useWorkspaceSettingsStore } from '@/modules/workspace/stores/workspace-settings-store'
import ApplicationStateBadge from '../components/application-state-badge.vue'
import { useApplicationStore } from '../stores/application-store'
import type { ApplicationTemplate, ApplicationTemplateState } from '../types'

const router = useRouter()
const applicationStore = useApplicationStore()
const workspaceSettingsStore = useWorkspaceSettingsStore()

const stateTabs: { key: ApplicationTemplateState; label: string }[] = [
  { key: 'published', label: 'Published' },
  { key: 'draft', label: 'Drafted' },
  { key: 'archived', label: 'Archived' },
]

const activeTab = ref<ApplicationTemplateState>('published')

const templates = computed(() =>
  [...applicationStore.templates]
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((t) => t.state === activeTab.value),
)

// Legacy zero_data copy: "There are no applications published/drafted/archived"
// (applications/sets/_sets.html.erb, state downcased)
const emptyTitle = computed(() => {
  const state = activeTab.value === 'draft' ? 'drafted' : activeTab.value

  return `There are no applications ${state}`
})

// Legacy notice (sets/index.html.erb): shown unless the group has a default
// application set. The setting lives on the workspace company settings page,
// the SPA counterpart of the legacy configuration link.
const missingDefaultApplication = computed(
  () => workspaceSettingsStore.workspace !== null && !workspaceSettingsStore.workspace.default_application_set_id,
)

// Legacy date format (Time::DATE_FORMATS[:mmddyyyy] = '%m/%d/%Y')
const formatDate = (iso: string): string =>
  `${String(new Date(iso).getMonth() + 1).padStart(2, '0')}/${String(new Date(iso).getDate()).padStart(2, '0')}/${new Date(iso).getFullYear()}`

///////////////////////////////////////////////////////////////////////////////
// Create
///////////////////////////////////////////////////////////////////////////////

const createModal = useModal()
const newName = ref('')
const creating = ref(false)

const openCreate = () => {
  newName.value = ''
  createModal.open()
}

const create = async () => {
  const name = newName.value.trim()
  if (!name || creating.value) return

  creating.value = true
  try {
    const template = await applicationStore.createTemplate(name)
    createModal.close()
    router.push({ name: 'applications.builder', params: { setId: template.id } })
  } catch {
    // error already surfaced via { silent: false }
  } finally {
    creating.value = false
  }
}

///////////////////////////////////////////////////////////////////////////////
// Lifecycle & delete
///////////////////////////////////////////////////////////////////////////////

// guards double-clicks and swallows rejections (the error toast already
// surfaced via { silent: false })
const lifecycleRunning = ref(false)
const runLifecycle = async (action: () => Promise<void>) => {
  if (lifecycleRunning.value) return

  lifecycleRunning.value = true
  try {
    await action()
  } catch {
    // error already surfaced via { silent: false }
  } finally {
    lifecycleRunning.value = false
  }
}

const deleting = ref<ApplicationTemplate | null>(null)
const deleteModal = useModal()

const openDelete = (template: ApplicationTemplate) => {
  deleting.value = template
  deleteModal.open()
}

const confirmDelete = async () => {
  if (!deleting.value) return

  try {
    await applicationStore.deleteTemplate(deleting.value.id)
    deleteModal.close()
    deleting.value = null
  } catch {
    // error already surfaced via { silent: false }
  }
}

const duplicate = async (template: ApplicationTemplate) => {
  const copy = await applicationStore.duplicateTemplate(template.id).catch(() => null)
  if (copy) router.push({ name: 'applications.builder', params: { setId: copy.id } })
}

onMounted(() => {
  applicationStore.fetchTemplates()
  // powers the missing-default-application notice; a failure keeps it hidden
  workspaceSettingsStore.fetch().catch(() => undefined)
})
</script>

<template>
  <TheLayout>
    <div class="flex flex-col gap-4">
      <header class="flex items-center justify-between">
        <h1 class="text-2xl font-bold text-gray-900">Entrepreneur Applications</h1>
        <VButton variant="v-blue" @click="openCreate">New Application</VButton>
      </header>

      <!-- legacy notice (sets/index.html.erb): shown unless the group has a
           default application set -->
      <p
        v-if="missingDefaultApplication"
        class="rounded-lg border border-[#3b88af]/30 bg-[#3b88af]/5 px-4 py-3 text-sm text-gray-700"
      >
        Your group has not setup a default application for companies to take during their application process. Please
        update your
        <router-link :to="{ name: 'workspace.settings.company' }" class="font-medium text-[#3b88af] hover:underline">
          configuration settings
        </router-link>
        if you wish for companies to take an application.
      </p>

      <div class="flex items-center gap-1">
        <button
          v-for="tab in stateTabs"
          :key="tab.key"
          class="rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
          :class="activeTab === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>

      <p v-if="applicationStore.loading" class="py-16 text-center text-sm text-gray-500">Loading applications…</p>

      <div
        v-else-if="applicationStore.error"
        class="rounded-xl border border-gray-200 bg-white py-16 text-center shadow-sm"
      >
        <p class="text-sm text-gray-500">Failed to load the applications. Please try again.</p>
      </div>

      <div
        v-else-if="templates.length === 0"
        class="rounded-xl border border-gray-200 bg-white py-16 text-center shadow-sm"
      >
        <p class="text-sm text-gray-500">{{ emptyTitle }}</p>
      </div>

      <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <div
          v-for="template in templates"
          :key="template.id"
          class="flex flex-col justify-between rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
        >
          <div>
            <div class="flex items-start justify-between gap-2">
              <router-link
                :to="{ name: 'applications.builder', params: { setId: template.id } }"
                class="font-semibold text-gray-900 hover:text-[#3b88af]"
              >
                {{ template.name }}
              </router-link>
              <ActionsMenu>
                <ActionsGroup>
                  <ActionItem
                    text="Open"
                    @click="router.push({ name: 'applications.builder', params: { setId: template.id } })"
                  />
                  <ActionItem text="Duplicate" @click="duplicate(template)" />
                  <ActionItem
                    v-if="template.state === 'draft'"
                    text="Publish"
                    @click="runLifecycle(() => applicationStore.publishTemplate(template.id))"
                  />
                  <ActionItem
                    v-if="template.state === 'published'"
                    text="Draft"
                    @click="runLifecycle(() => applicationStore.draftTemplate(template.id))"
                  />
                  <ActionItem
                    v-if="template.state !== 'archived'"
                    text="Archive"
                    @click="runLifecycle(() => applicationStore.archiveTemplate(template.id))"
                  />
                  <ActionItem text="Delete" @click="openDelete(template)" />
                </ActionsGroup>
              </ActionsMenu>
            </div>
            <!-- legacy card details table: Created + Last Updated (mm/dd/yyyy) -->
            <table class="mt-2 w-full text-xs text-gray-500">
              <tbody>
                <tr>
                  <td class="text-left">Created:</td>
                  <td class="text-right">{{ formatDate(template.created_at) }}</td>
                </tr>
                <tr>
                  <td class="text-left">Last Updated:</td>
                  <td class="text-right">{{ formatDate(template.updated_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="mt-4">
            <ApplicationStateBadge :state="template.state" />
          </div>
        </div>
      </div>

      <!-- Create modal -->
      <VPopUpModal :modalStore="createModal" widthClass="max-w-md w-full">
        <div class="flex flex-col gap-4">
          <h2 class="text-lg font-bold text-gray-900">New Application</h2>
          <input
            v-model="newName"
            type="text"
            placeholder="Application name"
            class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#3b88af]"
            @keyup.enter="create"
          />
          <div class="flex justify-end gap-2">
            <VButton variant="secondary" @click="createModal.close()">Cancel</VButton>
            <VButton variant="v-blue" :loading="creating" @click="create">Create</VButton>
          </div>
        </div>
      </VPopUpModal>

      <!-- Delete confirm modal -->
      <VPopUpModal :modalStore="deleteModal" widthClass="max-w-md w-full">
        <div class="flex flex-col items-center gap-4 text-center">
          <h2 class="text-lg font-bold text-gray-900">Delete Application</h2>
          <p class="text-sm text-gray-500">
            Are you sure you want to delete "{{ deleting?.name }}"? This cannot be undone.
          </p>
          <div class="flex justify-center gap-2">
            <VButton variant="secondary" @click="deleteModal.close()">Cancel</VButton>
            <VButton variant="v-red" @click="confirmDelete">Delete</VButton>
          </div>
        </div>
      </VPopUpModal>
    </div>
  </TheLayout>
</template>
